import React, {
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { Col, Modal, Row } from "antd";
import { ModalProps } from "antd/es/modal";
import { FormattedMessage } from "react-intl";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react/swiper-react.js";
import styled from "styled-components";
import styles from "src/styles";

// Contexts
import { OffsetLayoutContext } from "src/contexts/layout.context";

// Helpers
import { calculateLayout } from "src/helpers/ui.helper";

// Components
import SpecialDealCarCard from "src/components/home/SpecialDealCarCard";
import CarSearchCard, {
    CarSearchForm,
} from "src/components/booking/CarSearchCard";

// Assets
import { ReactComponent as ArrowLeftIcon } from "src/assets/icons/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "src/assets/icons/arrow-right.svg";

type Props = {
    dataSource?: Car[];
    locationOptions?: SelectOption[];
    fetchAddressOptions: (value?: string) => Promise<SelectOption[]>;
    onClick?: (values: CarSearchForm, item: Car) => void;
};

const responsiveConfig: Record<ScreenSize, any> = {
    xs: {
        breakpoint: 1,
        slidesPerView: 1.2,
        spaceBetween: 12,
    },
    sm: {
        breakpoint: 576,
        slidesPerView: 2.3,
        spaceBetween: 12,
    },
    md: {
        breakpoint: 768,
        slidesPerView: 2.3,
        spaceBetween: 20,
    },
    lg: {
        breakpoint: 992,
        slidesPerView: 3.3,
        spaceBetween: 30,
    },
    xl: {
        breakpoint: 1200,
        slidesPerView: 4.3,
        spaceBetween: 20,
    },
    xxl: {
        breakpoint: 1600,
        slidesPerView: 5.3,
        spaceBetween: 30,
    },
};

type SwipeNavigationType = {
    slideNext?: () => void;
    slidePrev?: () => void;
};

const SwipeNavigation = forwardRef<SwipeNavigationType>(
    (props: React.HTMLAttributes<HTMLButtonElement>, ref) => {
        const swiper = useSwiper();
        useImperativeHandle(ref, () => ({
            slideNext: () => swiper.slideNext(),
            slidePrev: () => swiper.slidePrev(),
        }));

        return null;
    }
);

const SpecialDealsCarousel = ({
    dataSource = [],
    locationOptions = [],
    fetchAddressOptions,
    onClick = () => {},
}: Props) => {
    const offsetLayout = useContext(OffsetLayoutContext);
    const swipeNavRef = useRef<SwipeNavigationType>(null);
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [screen, setScreen] = useState<ScreenSize | undefined>(undefined);
    const layout = calculateLayout(offsetLayout, 24);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [selectedCar, setSelectedCar] = useState<Car | undefined>(undefined);

    useEffect(() => {
        const handleResize = () => {
            const activeScreen = Object.keys(responsiveConfig)
                .reverse()
                .find(
                    (key) =>
                        responsiveConfig[key as ScreenSize].breakpoint <=
                        window.innerWidth
                );
            setScreen(activeScreen as ScreenSize);
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleNext = () => {
        swipeNavRef.current?.slideNext?.();
    };

    const handlePrevious = () => {
        swipeNavRef.current?.slidePrev?.();
    };

    const handleSlideChanged = (swiper: any) => {
        setCurrentSlide(swiper.activeIndex);
    };

    const handleClickReserve = (item: Car) => {
        setSelectedCar(item);
        setOpenModal(true);
    };

    const slidesPerView = Math.round(
        responsiveConfig[screen as ScreenSize]?.slidesPerView
    );

    const breakpoints = useMemo(
        () =>
            Object.keys(responsiveConfig).reduce((result, key) => {
                const item = responsiveConfig[key as ScreenSize];
                const { breakpoint, ...obj } = item;

                return {
                    ...result,
                    [breakpoint]: {
                        ...obj,
                        slidesPerView:
                            obj.slidesPerView >= dataSource.length
                                ? Math.floor(obj.slidesPerView)
                                : obj.slidesPerView,
                    },
                };
            }, {}),
        [dataSource.length]
    );

    return (
        <>
            <DivStyled className="special-deals-carousel">
                <Row>
                    <Col {...layout}>
                        <Row justify="space-between" align="middle">
                            <Col xs={18} md={16}>
                                <span className="title">
                                    <FormattedMessage id="specialDeals.title" />
                                </span>
                            </Col>
                            <Col xs={6} md={8} className="nav-actions">
                                <div>
                                    <ArrowLeftIcon
                                        className={
                                            currentSlide === 0
                                                ? "disabled"
                                                : undefined
                                        }
                                        onClick={handlePrevious}
                                    />
                                </div>
                                <div>
                                    <ArrowRightIcon
                                        className={
                                            currentSlide >=
                                            dataSource.length - slidesPerView
                                                ? "disabled"
                                                : undefined
                                        }
                                        onClick={handleNext}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row>
                    <Col
                        xs={{
                            offset: offsetLayout.xs,
                            span: 24 - offsetLayout.xs,
                        }}
                        sm={{
                            offset: offsetLayout.sm,
                            span: 24 - offsetLayout.sm,
                        }}
                        md={{
                            offset: offsetLayout.md,
                            span: 24 - offsetLayout.md,
                        }}
                        lg={{
                            offset: offsetLayout.lg,
                            span: 24 - offsetLayout.lg,
                        }}
                        xl={{
                            offset: offsetLayout.xl,
                            span: 24 - offsetLayout.xl,
                        }}
                        xxl={{
                            offset: offsetLayout.xxl,
                            span: 24 - offsetLayout.xxl,
                        }}
                    >
                        <Swiper
                            onSlideChange={handleSlideChanged}
                            breakpoints={breakpoints}
                        >
                            <SwipeNavigation ref={swipeNavRef} />
                            {dataSource.map((item: Car) => (
                                <SwiperSlide key={item.id}>
                                    <SpecialDealCarCard
                                        {...item}
                                        onClick={() => handleClickReserve(item)}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Col>
                </Row>
            </DivStyled>
            <ModalStyled
                className="car-search-modal"
                centered={true}
                closable={false}
                destroyOnClose={true}
                footer={false}
                maskClosable={true}
                visible={openModal}
                width="80%"
                onCancel={() => setOpenModal(false)}
            >
                <CarSearchCard
                    formName="specialDealsModal"
                    locationOptions={locationOptions}
                    fetchAddressOptions={fetchAddressOptions}
                    onSubmit={(values) => onClick(values, selectedCar as Car)}
                />
            </ModalStyled>
        </>
    );
};

const DivStyled = styled.div`
    &.special-deals-carousel {
        .title {
            font-style: normal;
            font-weight: 500;
            font-size: 32px;
            line-height: 1.6;
            color: ${styles.fontColorWhite};

            @media screen and (max-width: 767px) {
                font-size: 24px;
            }
        }

        .nav-actions {
            display: flex;
            justify-content: flex-end;

            & > div:last-child {
                margin-left: 40px;
            }

            svg {
                cursor: pointer;

                path {
                    fill: ${styles.fontColorWhite};
                }

                &.disabled {
                    cursor: not-allowed;

                    path {
                        fill: ${styles.fontColorDarkGray};
                    }
                }
            }
        }

        .swiper {
            margin-top: 70px;

            @media screen and (max-width: 767px) {
                margin-top: 30px;
            }
        }
    }
`;

const ModalStyled = styled(Modal)<ModalProps & { children: React.ReactChild }>`
    &.car-search-modal {
        .ant-modal-body {
            padding: 0;
        }
    }
`;

export default SpecialDealsCarousel;
